import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Form,
  FormFeedback,
  Input,
  Label,
  Spinner,
} from "reactstrap";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as Yup from "yup";
import { useDispatch } from "react-redux";

import AuthSplitLayout from "../../Components/Auth/AuthSplitLayout";
import GoogleAuthButton from "../../Components/Common/GoogleAuthButton";
import withRouter from "../../Components/Common/withRouter";
import {
  applyAuthValidationDetails,
  digitsOnly,
  formatCountdown,
  isAuthRateLimited,
  resolveAuthClientError,
} from "../../helpers/authClientError";
import {
  getPreferRegisterEmailCode,
  setPreferRegisterEmailCode,
} from "../../helpers/authLoginPreference";
import { loginSuccess } from "../../slices/auth/login/reducer";
import {
  getAuthSession,
  register as authRegister,
  requestEmailCode,
} from "../../services/authService";
import {
  clearIdentitySession,
  getIdentitySession,
} from "../../helpers/identity_storage";
import {
  consumeGoogleSignupDraft,
  GoogleSignupDraft,
} from "./GoogleCallback";
const VALID_PLAN_INTENTS = ["solo", "studio", "multi", "free"] as const;
const OTP_RESEND_COOLDOWN_SECONDS = 60;

type PlanIntent = (typeof VALID_PLAN_INTENTS)[number];

const Register = (props: any) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [passwordShow, setPasswordShow] = useState<boolean>(false);
  const [confirmPasswordShow, setConfirmPasswordShow] = useState<boolean>(false);
  const [loader, setLoader] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [emailTakenHint, setEmailTakenHint] = useState<boolean>(false);
  const [useEmailCode, setUseEmailCode] = useState<boolean>(() =>
    getPreferRegisterEmailCode()
  );
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [emailHint, setEmailHint] = useState<string>("");
  const [resendCooldown, setResendCooldown] = useState<number>(0);
  const [showSpamHint, setShowSpamHint] = useState<boolean>(false);
  const [googleDraft, setGoogleDraft] = useState<GoogleSignupDraft | null>(
    () =>
      searchParams.get("google") === "1" ||
      searchParams.get("continue") === "1"
        ? consumeGoogleSignupDraft()
        : null
  );

  const planIntent = useMemo((): PlanIntent | null => {
    const raw = (searchParams.get("plan") || "").toLowerCase().trim();

    return VALID_PLAN_INTENTS.includes(raw as PlanIntent)
      ? (raw as PlanIntent)
      : null;
  }, [searchParams]);

  const passwordSchema = useMemo(
    () =>
      Yup.object({
        studio_name: Yup.string().required(
          t("auth.register.validation.studioNameRequired")
        ),
        name: Yup.string().required(t("auth.register.validation.nameRequired")),
        email: Yup.string()
          .email(t("auth.register.validation.emailInvalid"))
          .required(t("auth.register.validation.emailRequired")),
        password: Yup.string()
          .min(8, t("auth.register.validation.passwordMin"))
          .required(t("auth.register.validation.passwordRequired")),
        password_confirmation: Yup.string()
          .oneOf(
            [Yup.ref("password")],
            t("auth.register.validation.passwordConfirmed")
          )
          .required(t("auth.register.validation.passwordConfirmed")),
      }),
    [t]
  );

  const emailCodeSchema = useMemo(
    () =>
      Yup.object({
        studio_name: Yup.string().required(
          t("auth.register.validation.studioNameRequired")
        ),
        name: Yup.string().required(t("auth.register.validation.nameRequired")),
        email: Yup.string()
          .email(t("auth.register.validation.emailInvalid"))
          .required(t("auth.register.validation.emailRequired")),
      }),
    [t]
  );

  const otpSchema = useMemo(
    () =>
      Yup.object({
        code: Yup.string()
          .required(t("auth.register.validation.otpRequired"))
          .matches(/^\d{6}$/, t("auth.register.validation.otpFormat")),
      }),
    [t]
  );

  const presentError = (
    message: string,
    options?: { details?: Record<string, string[]> }
  ) => {
    setEmailTakenHint(Boolean(options?.details?.email?.[0]));
    setErrorMessage(message);
    toast.error(message);
  };

  const finishRegisteredSession = (message?: string) => {
    const pendingBefore = (getIdentitySession()?.memberships || []).filter(
      (item) => item.status === "pending"
    );
    const session = getAuthSession();

    if (session) {
      if (message) {
        toast.success(message);
      }

      if (pendingBefore.length === 1) {
        const studioName =
          pendingBefore[0].tenant_name ||
          t("auth.select_studio.unnamed");
        toast.info(
          t("auth.register.toast.pendingInviteRemains", { name: studioName })
        );
      } else if (pendingBefore.length > 1) {
        toast.info(
          t("auth.register.toast.pendingInvitesRemain", {
            count: pendingBefore.length,
          })
        );
      }

      clearIdentitySession();
      dispatch(loginSuccess(session));
      props.router.navigate("/dashboard?welcome=app");
      return true;
    }

    return false;
  };

  const otpForm = useFormik({
    enableReinitialize: true,
    initialValues: { code: "" },
    validationSchema: otpSchema,
    onSubmit: async (values) => {
      if (!challengeId) {
        return;
      }

      setLoader(true);
      setErrorMessage("");
      setEmailTakenHint(false);
      setShowSpamHint(false);

      try {
        const response = await authRegister({
          studio_name: validation.values.studio_name,
          name: validation.values.name,
          email: validation.values.email,
          email_challenge_id: challengeId,
          email_code: values.code,
          ...(planIntent ? { plan_intent: planIntent } : {}),
        });

        if (response.success && finishRegisteredSession(response.message)) {
          return;
        }

        presentError(
          response.message ||
            response.error?.message ||
            t("auth.register.toast.genericError"),
          { details: response.error?.details }
        );
      } catch (error: unknown) {
        const resolved = resolveAuthClientError(
          error,
          t("auth.register.toast.genericError")
        );

        if (
          resolved.code === "OTP_EXPIRED" ||
          resolved.code === "OTP_LOCKED"
        ) {
          setResendCooldown(0);
          otpForm.setFieldValue("code", "");
          presentError(
            resolved.code === "OTP_EXPIRED"
              ? t("auth.register.toast.otpExpired")
              : t("auth.register.toast.otpLocked")
          );
          return;
        }

        if (resolved.code === "OTP_INVALID") {
          otpForm.setFieldValue("code", "");
          presentError(t("auth.register.toast.otpInvalid"));
          return;
        }

        presentError(
          isAuthRateLimited(resolved)
            ? t("auth.register.toast.rateLimited")
            : resolved.message,
          { details: resolved.details }
        );
      } finally {
        setLoader(false);
      }
    },
  });

  const requestSignupOtp = async (email: string): Promise<boolean> => {
    const response = await requestEmailCode(email, "signup");

    if (response.success && response.data?.challenge_id) {
      setChallengeId(response.data.challenge_id);
      setEmailHint(response.data.email_hint || email);
      setResendCooldown(OTP_RESEND_COOLDOWN_SECONDS);
      setShowSpamHint(true);
      otpForm.resetForm();
      setErrorMessage("");
      toast.success(response.message || t("auth.register.toast.otpSent"));
      return true;
    }

    presentError(
      response.message ||
        response.error?.message ||
        t("auth.register.toast.genericError"),
      { details: response.error?.details }
    );
    return false;
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      studio_name: "",
      name: googleDraft?.name || "",
      email:
        googleDraft?.email ||
        new URLSearchParams(window.location.search).get("email") ||
        "",
      password: "",
      password_confirmation: "",
    },
    validationSchema: googleDraft
      ? Yup.object({
          studio_name: Yup.string().required(
            t("auth.register.validation.studioNameRequired")
          ),
          name: Yup.string().required(t("auth.register.validation.nameRequired")),
          email: Yup.string()
            .email(t("auth.register.validation.emailInvalid"))
            .required(t("auth.register.validation.emailRequired")),
        })
      : useEmailCode
        ? emailCodeSchema
        : passwordSchema,
    onSubmit: async (values, helpers) => {
      setLoader(true);
      setErrorMessage("");
      setEmailTakenHint(false);

      const showFormError = (
        message: string,
        details?: Record<string, string[]>
      ) => {
        presentError(message, { details });
        applyAuthValidationDetails(
          details,
          helpers.setFieldError,
          helpers.setFieldTouched,
          [
            "studio_name",
            "name",
            "email",
            "password",
            "password_confirmation",
          ]
        );
      };

      try {
        if (googleDraft) {
          const response = await authRegister({
            studio_name: values.studio_name,
            name: values.name,
            email: values.email,
            identity_token: googleDraft.identity_token,
            ...(planIntent ? { plan_intent: planIntent } : {}),
          });

          if (response.success && finishRegisteredSession(response.message)) {
            setGoogleDraft(null);
            return;
          }

          showFormError(
            response.message || t("auth.register.toast.genericError"),
            response.error?.details
          );
          return;
        }

        if (useEmailCode) {
          await requestSignupOtp(values.email);
          return;
        }

        const response = await authRegister({
          ...values,
          ...(planIntent ? { plan_intent: planIntent } : {}),
        });

        if (response.success && finishRegisteredSession(response.message)) {
          return;
        }

        showFormError(
          response.message || t("auth.register.toast.genericError"),
          response.error?.details
        );
      } catch (error: unknown) {
        const resolved = resolveAuthClientError(
          error,
          t("auth.register.toast.genericError")
        );
        showFormError(
          isAuthRateLimited(resolved)
            ? t("auth.register.toast.rateLimited")
            : resolved.message,
          resolved.details
        );
      } finally {
        setLoader(false);
      }
    },
  });

  const resetOtpChallenge = () => {
    setChallengeId(null);
    setEmailHint("");
    setResendCooldown(0);
    setShowSpamHint(false);
    otpForm.resetForm();
    setErrorMessage("");
    setEmailTakenHint(false);
  };

  const toggleEmailCodeMode = (next: boolean) => {
    setUseEmailCode(next);
    setPreferRegisterEmailCode(next);
    resetOtpChallenge();
    setEmailTakenHint(false);
    validation.setFieldValue("password", "");
    validation.setFieldValue("password_confirmation", "");
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0 || loader || !validation.values.email) {
      return;
    }

    setLoader(true);
    setErrorMessage("");
    setEmailTakenHint(false);

    try {
      await requestSignupOtp(validation.values.email);
    } catch (error: unknown) {
      const resolved = resolveAuthClientError(
        error,
        t("auth.register.toast.genericError")
      );
      presentError(
        isAuthRateLimited(resolved)
          ? t("auth.register.toast.rateLimited")
          : resolved.message,
        { details: resolved.details }
      );
    } finally {
      setLoader(false);
    }
  };

  const handleOtpInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (showSpamHint) {
      setShowSpamHint(false);
    }
    otpForm.setFieldValue("code", digitsOnly(event.target.value), true);
  };

  const handleOtpPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    if (showSpamHint) {
      setShowSpamHint(false);
    }
    otpForm.setFieldValue(
      "code",
      digitsOnly(event.clipboardData.getData("text")),
      true
    );
  };

  useEffect(() => {
    document.title = t("auth.register.pageTitle");
  }, [t]);

  useEffect(() => {
    if (resendCooldown <= 0) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setResendCooldown((current) => Math.max(0, current - 1));
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [resendCooldown]);

  const formTitle = challengeId
    ? t("auth.register.otp.title")
    : t("auth.register.title");
  const formSubtitle = challengeId
    ? t("auth.register.otp.subtitle", { email: emailHint })
    : useEmailCode
      ? t("auth.register.otp.requestSubtitle")
      : t("auth.register.subtitle");

  return (
    <React.Fragment>
      <ToastContainer closeButton={false} />
      <AuthSplitLayout
        title={formTitle}
        subtitle={formSubtitle}
        headerAction={
          <Link to="/login" className="adatis-auth-split__header-btn">
            <i className="ri-login-box-line" aria-hidden="true" />
            {t("auth.register.links.signIn")}
          </Link>
        }
        lead={
          <>
            {useEmailCode ? (
              <nav
                className="d-flex justify-content-start gap-2"
                aria-label={t("auth.register.steps.label")}
              >
                <span
                  className={`badge ${
                    challengeId ? "bg-light text-muted" : "bg-primary"
                  }`}
                  aria-current={!challengeId ? "step" : undefined}
                >
                  {t("auth.register.steps.data")}
                </span>
                <span
                  className="text-muted align-self-center"
                  aria-hidden="true"
                >
                  →
                </span>
                <span
                  className={`badge ${
                    challengeId ? "bg-primary" : "bg-light text-muted"
                  }`}
                  aria-current={challengeId ? "step" : undefined}
                >
                  {t("auth.register.steps.code")}
                </span>
              </nav>
            ) : null}
            {planIntent && !challengeId ? (
              <Alert color="primary" className="mb-0 mt-2">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <Badge color="primary" className="text-uppercase">
                    {t(`auth.register.planIntent.badge`)}
                  </Badge>
                  <span className="fw-semibold">
                    {t(`auth.register.planIntent.names.${planIntent}`)}
                  </span>
                </div>
                <p className="mb-0 small">
                  {t("auth.register.planIntent.trialHint")}
                </p>
              </Alert>
            ) : null}
            {errorMessage ? (
              <Alert
                color="danger"
                className={`mb-0 ${useEmailCode || planIntent ? "mt-2" : ""}`}
                role="alert"
                aria-live="assertive"
              >
                <div>{errorMessage}</div>
                {emailTakenHint ? (
                  <div className="mt-2 mb-0 d-flex flex-wrap gap-3">
                    <Link
                      to={`/login?email=${encodeURIComponent(
                        validation.values.email || ""
                      )}`}
                      className="alert-link fw-semibold"
                    >
                      {t("auth.register.links.signInWithEmail")}
                    </Link>
                    <Link
                      to={`/forgot-password?email=${encodeURIComponent(
                        validation.values.email || ""
                      )}`}
                      className="alert-link"
                    >
                      {t("auth.register.links.forgotPassword")}
                    </Link>
                  </div>
                ) : null}
              </Alert>
            ) : null}
            {challengeId && showSpamHint ? (
              <Alert
                color="info"
                className="mt-2 mb-0"
                role="status"
                aria-live="polite"
              >
                {t("auth.register.otp.spamHint")}
              </Alert>
            ) : null}
          </>
        }
      >
        <>
                      {challengeId ? (
                        <Form
                          onSubmit={(event) => {
                            event.preventDefault();
                            otpForm.handleSubmit();
                          }}
                        >
                          <div className="mb-3">
                            <Label
                              htmlFor="register-otp"
                              className="form-label"
                            >
                              {t("auth.register.fields.otp.label")}
                            </Label>
                            <Input
                              id="register-otp"
                              name="code"
                              className="form-control text-center fs-4"
                              placeholder={t(
                                "auth.register.fields.otp.placeholder"
                              )}
                              type="text"
                              inputMode="numeric"
                              pattern="[0-9]*"
                              autoComplete="one-time-code"
                              autoFocus
                              maxLength={6}
                              onChange={handleOtpInputChange}
                              onPaste={handleOtpPaste}
                              onBlur={otpForm.handleBlur}
                              value={otpForm.values.code || ""}
                              invalid={
                                !!(otpForm.touched.code && otpForm.errors.code)
                              }
                            />
                            {otpForm.touched.code && otpForm.errors.code ? (
                              <FormFeedback type="invalid">
                                {otpForm.errors.code}
                              </FormFeedback>
                            ) : (
                              <div className="form-text">
                                {t("auth.register.otp.pasteHint")}
                              </div>
                            )}
                          </div>

                          <div className="mt-4">
                            <Button
                              color="primary"
                              disabled={loader}
                              className="btn btn-primary w-100"
                              type="submit"
                            >
                              {loader ? (
                                <>
                                  <Spinner size="sm" className="me-2" />
                                  {t("auth.register.actions.loading")}
                                </>
                              ) : (
                                t("auth.register.actions.verifyOtp")
                              )}
                            </Button>
                          </div>

                          <div className="mt-3 text-center">
                            <button
                              type="button"
                              className="btn btn-link"
                              disabled={loader || resendCooldown > 0}
                              onClick={handleResendCode}
                              aria-live="polite"
                            >
                              {resendCooldown > 0
                                ? t("auth.register.actions.resendIn", {
                                    time: formatCountdown(resendCooldown),
                                  })
                                : t("auth.register.actions.resendCode")}
                            </button>
                          </div>

                          <div className="mt-1 text-center">
                            <button
                              type="button"
                              className="btn btn-link text-muted"
                              onClick={resetOtpChallenge}
                            >
                              {t("auth.register.actions.changeEmail")}
                            </button>
                          </div>
                        </Form>
                      ) : (
                        <Form
                          onSubmit={(event) => {
                            event.preventDefault();
                            validation.handleSubmit();
                          }}
                        >
                          <div className="mb-3">
                            <Label htmlFor="studio_name" className="form-label">
                              {t("auth.register.fields.studioName.label")}
                            </Label>
                            <Input
                              id="studio_name"
                              name="studio_name"
                              className="form-control"
                              placeholder={t(
                                "auth.register.fields.studioName.placeholder"
                              )}
                              type="text"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.studio_name || ""}
                              invalid={
                                !!(
                                  validation.touched.studio_name &&
                                  validation.errors.studio_name
                                )
                              }
                            />
                            {validation.touched.studio_name &&
                            validation.errors.studio_name ? (
                              <FormFeedback type="invalid">
                                {validation.errors.studio_name}
                              </FormFeedback>
                            ) : null}
                          </div>

                          <div className="mb-3">
                            <Label htmlFor="name" className="form-label">
                              {t("auth.register.fields.name.label")}
                            </Label>
                            <Input
                              id="name"
                              name="name"
                              className="form-control"
                              placeholder={t(
                                "auth.register.fields.name.placeholder"
                              )}
                              type="text"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.name || ""}
                              invalid={
                                !!(
                                  validation.touched.name &&
                                  validation.errors.name
                                )
                              }
                            />
                            {validation.touched.name &&
                            validation.errors.name ? (
                              <FormFeedback type="invalid">
                                {validation.errors.name}
                              </FormFeedback>
                            ) : null}
                          </div>

                          <div className="mb-3">
                            <Label htmlFor="email" className="form-label">
                              {t("auth.register.fields.email.label")}
                            </Label>
                            <Input
                              id="email"
                              name="email"
                              className="form-control"
                              placeholder={t(
                                "auth.register.fields.email.placeholder"
                              )}
                              type="email"
                              onChange={validation.handleChange}
                              onFocus={() => {
                                if (errorMessage || emailTakenHint) {
                                  setErrorMessage("");
                                  setEmailTakenHint(false);
                                  validation.setFieldError("email", undefined);
                                }
                              }}
                              onBlur={validation.handleBlur}
                              value={validation.values.email || ""}
                              readOnly={Boolean(googleDraft)}
                              invalid={
                                !!(
                                  validation.touched.email &&
                                  validation.errors.email
                                )
                              }
                            />
                            {validation.touched.email &&
                            validation.errors.email ? (
                              <FormFeedback type="invalid">
                                {validation.errors.email}
                              </FormFeedback>
                            ) : null}
                          </div>

                          {!useEmailCode && !googleDraft ? (
                            <>
                              <div className="mb-3">
                                <Label
                                  className="form-label"
                                  htmlFor="password-input"
                                >
                                  {t("auth.register.fields.password.label")}
                                </Label>
                                <div className="position-relative auth-pass-inputgroup mb-3">
                                  <Input
                                    id="password-input"
                                    name="password"
                                    value={validation.values.password || ""}
                                    type={passwordShow ? "text" : "password"}
                                    className="form-control pe-5"
                                    placeholder={t(
                                      "auth.register.fields.password.placeholder"
                                    )}
                                    onChange={validation.handleChange}
                                    onBlur={validation.handleBlur}
                                    invalid={
                                      !!(
                                        validation.touched.password &&
                                        validation.errors.password
                                      )
                                    }
                                  />
                                  {validation.touched.password &&
                                  validation.errors.password ? (
                                    <FormFeedback type="invalid">
                                      {validation.errors.password}
                                    </FormFeedback>
                                  ) : null}
                                  <button
                                    className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                                    type="button"
                                    onClick={() =>
                                      setPasswordShow(!passwordShow)
                                    }
                                  >
                                    <i className="ri-eye-fill align-middle"></i>
                                  </button>
                                </div>
                              </div>

                              <div className="mb-3">
                                <Label
                                  className="form-label"
                                  htmlFor="password-confirmation-input"
                                >
                                  {t(
                                    "auth.register.fields.passwordConfirmation.label"
                                  )}
                                </Label>
                                <div className="position-relative auth-pass-inputgroup mb-3">
                                  <Input
                                    id="password-confirmation-input"
                                    name="password_confirmation"
                                    value={
                                      validation.values.password_confirmation ||
                                      ""
                                    }
                                    type={
                                      confirmPasswordShow
                                        ? "text"
                                        : "password"
                                    }
                                    className="form-control pe-5"
                                    placeholder={t(
                                      "auth.register.fields.passwordConfirmation.placeholder"
                                    )}
                                    onChange={validation.handleChange}
                                    onBlur={validation.handleBlur}
                                    invalid={
                                      !!(
                                        validation.touched
                                          .password_confirmation &&
                                        validation.errors.password_confirmation
                                      )
                                    }
                                  />
                                  {validation.touched.password_confirmation &&
                                  validation.errors.password_confirmation ? (
                                    <FormFeedback type="invalid">
                                      {
                                        validation.errors
                                          .password_confirmation
                                      }
                                    </FormFeedback>
                                  ) : null}
                                  <button
                                    className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                                    type="button"
                                    onClick={() =>
                                      setConfirmPasswordShow(
                                        !confirmPasswordShow
                                      )
                                    }
                                  >
                                    <i className="ri-eye-fill align-middle"></i>
                                  </button>
                                </div>
                              </div>
                            </>
                          ) : null}

                          <div className="mt-4">
                            <Button
                              color="primary"
                              disabled={loader}
                              className="btn btn-primary w-100"
                              type="submit"
                            >
                              {loader ? (
                                <>
                                  <Spinner size="sm" className="me-2" />
                                  {t("auth.register.actions.loading")}
                                </>
                              ) : useEmailCode && !googleDraft ? (
                                t("auth.register.actions.sendCode")
                              ) : (
                                t("auth.register.actions.submit")
                              )}
                            </Button>
                          </div>

                          {!googleDraft ? (
                            <>
                              <div className="mt-3">
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary w-100"
                                  onClick={() =>
                                    toggleEmailCodeMode(!useEmailCode)
                                  }
                                >
                                  {useEmailCode
                                    ? t("auth.register.actions.usePassword")
                                    : t("auth.register.actions.useEmailCode")}
                                </button>
                              </div>
                              <div className="mt-3 mb-0">
                                <GoogleAuthButton
                                  intent="signup"
                                  disabled={loader}
                                  className="btn-light"
                                />
                              </div>
                            </>
                          ) : (
                            <Alert color="info" className="mt-3 mb-0">
                              {searchParams.get("continue") === "1"
                                ? t("auth.register.studioContinueHint")
                                : t("auth.google.registerHint")}
                            </Alert>
                          )}
                        </Form>
                      )}
        </>
      </AuthSplitLayout>
    </React.Fragment>
  );
};

export default withRouter(Register);
